"use client";

import {
  execute,
  GET_USER_GEOLOCATION,
  getCookieUserLocation,
  QUERY_KEYS,
  saveUserLocation,
} from "@frontend/roz-sdk-core";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@frontend/roz-uikit/studio";
import { useQuery } from "@tanstack/react-query";
import { MapPinIcon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { cn } from "@/lib/cn";
import { calcGeoPlaceInfo } from "@/lib/region-helpers";
import { useSdkConfig } from "@/provider";
import { SheetContentType } from "@/types";
import { useSheetStore } from "@/ui-state";

export interface RegionSelectorProps {
  compactOnTablet?: boolean;
}

export const RegionSelector = ({
  compactOnTablet = false,
}: RegionSelectorProps) => {
  const [visible, setVisible] = useState(false);
  const { setType, open } = useSheetStore();
  const config = useSdkConfig();
  const isDarkTheme = config.theme === "dark";

  const cookieGeolocation = getCookieUserLocation();

  const { data, isLoading } = useQuery({
    queryFn: () => execute(GET_USER_GEOLOCATION),
    queryKey: QUERY_KEYS.userGeolocation,
    enabled: Boolean(!cookieGeolocation),
  });

  const backGeolocation = cookieGeolocation || data?.userGeoLocationLog;

  const { placeWord, showingPlace } = calcGeoPlaceInfo(backGeolocation);

  const isEmpty = useMemo(() => {
    if (isLoading || !backGeolocation) {
      return true;
    }

    return Object.values(backGeolocation)?.filter(Boolean).length === 0;
  }, [backGeolocation, isLoading]);

  useEffect(() => {
    if (!(isLoading || getCookieUserLocation())) {
      setVisible(true);
    }
  }, [isLoading]);

  const handleConfirm = () => {
    if (cookieGeolocation) {
      saveUserLocation(cookieGeolocation);
    } else {
      saveUserLocation(backGeolocation);
    }
    setVisible(false);
  };

  const handleSelectOther = () => {
    setType(SheetContentType.regionSelect);
    open();
    setVisible(false);
  };

  const renderRegionContent = () => {
    if (isEmpty) {
      return (
        <>
          <div className="sdkui:px-2 sdkui:py-1.5 sdkui:font-medium sdkui:text-dark-100 sdkui:text-sm sdkui:leading-5">
            Не удалось определить ваш {placeWord}
          </div>
          <DropdownMenuSeparator className="sdkui:bg-zinc-100" />
          <DropdownMenuItem
            className="sdkui:px-2 sdkui:py-1.5 sdkui:text-base"
            onClick={handleSelectOther}
          >
            Выбрать {placeWord}
          </DropdownMenuItem>
        </>
      );
    }

    return (
      <>
        <div className="sdkui:px-2 sdkui:py-1.5 sdkui:font-medium sdkui:text-dark-100 sdkui:text-sm sdkui:leading-5">
          Ваш {placeWord}: {showingPlace}
        </div>
        <DropdownMenuSeparator className="sdkui:bg-zinc-100" />
        <DropdownMenuItem
          className="sdkui:px-2 sdkui:py-1.5 sdkui:text-base"
          onClick={handleConfirm}
        >
          Да, верно
        </DropdownMenuItem>
        <DropdownMenuSeparator className="sdkui:bg-zinc-100" />
        <DropdownMenuItem
          className="sdkui:px-2 sdkui:py-1.5 sdkui:text-base"
          onClick={handleSelectOther}
        >
          Выбрать другой
        </DropdownMenuItem>
      </>
    );
  };

  return (
    <DropdownMenu modal={false} onOpenChange={setVisible} open={visible}>
      <DropdownMenuTrigger asChild>
        <Button
          className={cn(
            "sdkui:px-3 sdkui:font-medium sdkui:text-sm sdkui:lg:px-4",
            isDarkTheme
              ? "sdkui:text-white sdkui:hover:text-white sdkui:focus-visible:text-white sdkui:active:text-white"
              : "sdkui:text-dark-100"
          )}
          size="lg"
          variant="ghostPrimary"
        >
          <MapPinIcon className="sdkui:size-4" />
          <span
            className={cn(compactOnTablet && "sdkui:hidden sdkui:lg:inline")}
          >
            {showingPlace || "Регион"}
          </span>
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="start"
        className="sdkui:w-46 sdkui:2xl:w-50"
        sideOffset={10}
      >
        {renderRegionContent()}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
